import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles, {
  textStyle,
} from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Accessibility & Usability Resources:',
    cards: [
      {
        title: 'IBM Web Accessibility Checklist',
        subtitle: '[ibm.com]',
        linkUrl: REFERENCE_LINKS.ibmAccessibilityChecklist,
      },
      {
        title: 'Government usability testing resources',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovUsabilityTesting,
      },
      {
        title: 'Find your Section 508 Coordinator',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508ProgramManagerListing,
      },
    ],
  },
];

const IMAGES = '/images/branding-styleguide-interactions-and-animations';

const usageStates = [
  {
    title: 'Default',
    description:
      'The default button state is how a button appears or behaves on a user interface before any interaction.',
    image: `${IMAGES}/usage-states-types-01@2x.webp`,
    alt: 'Numbered 1: a primary button in its default state',
  },
  {
    title: 'Disabled',
    description: 'A disabled state communicates a noninteractive component or element.',
    image: `${IMAGES}/usage-states-types-02@2x.webp`,
    alt: 'Numbered 2: a button in its disabled state',
  },
  {
    title: 'Hover',
    description:
      'A hover state communicates when a user has placed a cursor above an interactive element.',
    image: `${IMAGES}/usage-states-types-03@2x.webp`,
    alt: 'Numbered 3: a button with a cursor placed above it',
  },
  {
    title: 'Active',
    description:
      'A focused state communicates when a user has highlighted an element, using an input method such as a keyboard or voice.',
    image: `${IMAGES}/usage-states-types-04@2x.webp`,
    alt: 'Numbered 4: a button in its active state',
  },
  {
    title: 'Selected',
    description: 'A selected state communicates a user choice.',
    image: `${IMAGES}/usage-states-types-05@2x.webp`,
    alt: 'Numbered 5: a columns menu with one option selected',
  },
  {
    title: 'Activated',
    description:
      'An activated state communicates a highlighted destination, whether initiated by the user or by default.',
    image: `${IMAGES}/usage-states-types-06@2x.webp`,
    alt: 'Numbered 6: a dashboard screen with its Overview tab activated',
  },
  {
    title: 'Error',
    description: 'An error state communicates a user or system mistake.',
    image: `${IMAGES}/usage-states-types-07@2x.webp`,
    alt: 'Numbered 7: an input showing an error message',
  },
];

const behaviorExamples = [
  {
    recommended: true,
    description:
      "Disabled components can be hovered over, but won't display state changes to the component or cursor.",
    image: `${IMAGES}/screen-behavior-01@2x.webp`,
    height: 272,
    alt: 'A disabled button with a cursor over it, unchanged',
  },
  {
    recommended: true,
    description: "Disabled components don't change state when tapped.",
    image: `${IMAGES}/screen-behavior-02@2x.webp`,
    height: 272,
    alt: 'A disabled button, unchanged when tapped',
  },
  {
    recommended: false,
    description: 'Disabled components cannot be hovered, focused or clicked.',
    image: `${IMAGES}/screen-behavior-03@2x.webp`,
    height: 336,
    alt: 'Disabled buttons that show a hover and a focus state',
  },
  {
    recommended: false,
    description: 'Disabled components cannot be dragged or pressed.',
    image: `${IMAGES}/screen-behavior-04@2x.webp`,
    height: 336,
    alt: 'A disabled input that shows a pressed state',
  },
];

const InteractionsAndAnimationsStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Interactions and Animations"
        breadcrumbs={[
          { label: 'Branding Styleguide', url: '/branding-styleguide' },
          { label: 'Interactions and Animations' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="principles">
                  Principles
                </h3>
                <p className="mb-40">
                  When designing interactions and animations, focus on creating purposeful and
                  seamless experiences that enhance the user interface without overwhelming it.
                  Consider the needs of your audience and ensure all elements align with the
                  product&apos;s visual identity. Animations should be subtle yet functional,
                  guiding users through tasks by providing feedback, such as confirming actions or
                  transitioning between states. Each interaction should feel intuitive, maintaining
                  visual consistency and clearly communicating status changes without drastically
                  altering components. Prioritize accessibility by offering motion-reduction options
                  and optimizing performance to ensure smooth and responsive animations across
                  devices. By adhering to these principles, you create an inclusive, engaging, and
                  user-friendly experience.
                </p>
              </div>
              <h2 className="h1 divider" id="specifications">
                Specifications
              </h2>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="usage-states-types">
                  Usage
                </div>
                <h3 className="h3 mb-40">Usage &amp; states types</h3>
              </div>
              <div className="number-cards">
                {usageStates.map((state) => (
                  <div className="number-card" key={state.title}>
                    <div className="number-card-img">
                      <img src={state.image} alt={state.alt} />
                    </div>
                    <div className="number-card-desc">
                      <strong>{state.title}</strong>
                      <p>{state.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <h2 className="divider h1" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="behavior">
                  Behavior
                </h3>
                <p>
                  Ensure interactions and animations align with user expectations, providing smooth
                  and purposeful transitions. Animations should highlight important actions or
                  changes without distracting the user. Disabled components remain static, with no
                  change in state when tapped or hovered over, signaling their inactive status.
                </p>
                <p className="mb-40">
                  Consistent timing and easing curves create a cohesive feel, while accessible
                  design allows for reduced motion settings to accommodate all users. Thoughtful
                  behavior enhances usability and ensures a seamless, intuitive experience.
                </p>
              </div>
              <div className="ex-cards">
                {behaviorExamples.map((example) => (
                  <div className={`ex-card ${example.recommended}`} key={example.description}>
                    <img
                      className="ex-card-img"
                      src={example.image}
                      width={488}
                      height={example.height}
                      alt={example.alt}
                    />
                    <div className="ex-card-desc">
                      {example.recommended ? (
                        <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                          Do
                        </BadgeStyled>
                      ) : (
                        <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                          Don&rsquo;t
                        </BadgeStyled>
                      )}
                      <p>{example.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <h2 id="references" className="divider h1">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'principles',
                    'specifications',
                    'usage-states-types',
                    'guidelines',
                    'behavior',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/branding-styleguide/imagery"
        previous="Imagery"
        nextLink="/branding-styleguide/brand-in-use"
        next="Brand in Use"
      />
    </div>
  </div>
);

const InteractionsAndAnimations = styled(InteractionsAndAnimationsStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 4, 6])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }

  /* The state titles of the Updated frame (13608:136481) are H4 SB 14px, not the 16px the shared
     number cards use on the pages that have no Updated frame. */
  .number-cards strong {
    ${textStyle('h4Sb')}
  }
`;

export default InteractionsAndAnimations;
